import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, MessageCircle, Archive, ArchiveRestore, PowerOff, ArrowLeft, Megaphone, ShieldOff, ShieldCheck } from "lucide-react";
import ReportBlockMenu from "@/components/ReportBlockMenu";
import { SiteShell } from "@/components/layout/SiteShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";

export default function Messages() {
  const { user } = useAuth();
  const { threadId } = useParams();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active"); // active | archived

  const loadThreads = async () => {
    try { const { data } = await api.get("/threads"); setThreads(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadThreads(); }, []);
  useEffect(() => {
    const id = setInterval(loadThreads, 8000);
    return () => clearInterval(id);
  }, []);

  return (
    <SiteShell showFooter={false}>
      <section className="container-page py-8 md:py-10 overflow-x-hidden" data-testid={TID.messages.root}>
        <h1 className="font-serif text-4xl tracking-tight">Messages</h1>
        <p className="mt-2 text-muted-foreground">Talk directly with the people you&apos;re building stories with.</p>

        <div className="mt-8 grid lg:grid-cols-[320px_1fr] gap-6 min-h-[60vh]">
          {/* Conversation list — full-width on mobile when no thread is open,
              hidden on mobile when a thread is open (list/detail pattern). */}
          <div className={`min-w-0 ${threadId ? "hidden lg:block" : "block"}`} data-testid="messages-list-pane">
            <ThreadList threads={threads} loading={loading} activeId={threadId} currentUserId={user?.id} tab={tab} setTab={setTab} onUpdated={loadThreads} />
          </div>
          {/* Conversation pane — hidden on mobile until a thread is picked. */}
          <div
            className={`card-soft p-0 overflow-hidden min-w-0 ${threadId ? "block" : "hidden lg:block"}`}
            data-testid="messages-detail-pane"
          >
            {threadId ? <ThreadView threadId={threadId} onUpdated={loadThreads} /> : <EmptyPane />}
          </div>
        </div>
      </section>
    </SiteShell>
  );
}

function ThreadList({ threads, loading, activeId, currentUserId, tab, setTab, onUpdated }) {
  const { active, archived } = useMemo(() => {
    const a = []; const ar = [];
    for (const t of threads) ((t.status || "active") === "archived" || t.inbox_archived ? ar : a).push(t);
    return { active: a, archived: ar };
  }, [threads]);
  const visible = tab === "archived" ? archived : active;
  return (
    <aside className="card-soft p-0 overflow-hidden">
      <div className="p-4 border-b">
        <h2 className="font-serif text-lg">Conversations</h2>
        <div className="mt-3 inline-flex rounded-full border p-0.5 text-xs">
          <button data-testid="messages-tab-active" onClick={() => setTab("active")}
            className={`px-3 py-1 rounded-full transition ${tab === "active" ? "bg-[hsl(var(--sb-forest))] text-white" : "text-muted-foreground"}`}>
            Active {active.length > 0 && <span className="ml-1 opacity-75">{active.length}</span>}
          </button>
          <button data-testid="messages-tab-archived" onClick={() => setTab("archived")}
            className={`px-3 py-1 rounded-full transition ${tab === "archived" ? "bg-[hsl(var(--sb-forest))] text-white" : "text-muted-foreground"}`}>
            Archived {archived.length > 0 && <span className="ml-1 opacity-75">{archived.length}</span>}
          </button>
        </div>
      </div>
      <ul className="max-h-[70vh] overflow-y-auto">
        {loading && Array.from({ length: 4 }).map((_, i) => <li key={i} className="h-16 border-b animate-pulse bg-muted/40" />)}
        {!loading && visible.length === 0 && (
          <li className="p-8 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-[hsl(var(--sb-moss))] grid place-items-center">
              {tab === "archived" ? <Archive size={22} className="text-[hsl(var(--sb-forest-2))]" /> : <MessageCircle size={22} className="text-[hsl(var(--sb-forest-2))]" />}
            </div>
            <p className="mt-4 font-serif text-lg">
              {tab === "archived" ? "No archived chats yet." : "No conversations yet."}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {tab === "archived"
                ? "Ended chats and archived Community Replies are kept here for reference."
                : "Start one from any project page — it's how great collaborations begin."}
            </p>
            {tab !== "archived" && <Link to="/jobs" className="btn-ghost border mt-4 inline-flex text-xs">Browse projects</Link>}
          </li>
        )}
        {visible.map((t) => {
          const other = t.participants.find((p) => p.id !== currentUserId) || t.participants[0];
          const unreadCount = (t.unread || {})[currentUserId] || 0;
          const active = t.id === activeId;
          const isArchived = (t.status || "active") === "archived";
          const isCommunity = t.origin === "community_update";
          return (
            <li key={t.id} className={`border-b flex items-center min-w-0 ${active ? "bg-[hsl(var(--sb-moss))]/50" : ""}`}>
              <Link to={`/messages/${t.id}`} data-testid={TID.messages.threadItem} className="flex flex-1 gap-3 p-4 items-center hover:bg-muted/40 transition-colors min-w-0">
                <ParticipantAvatar participant={other} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2 min-w-0">
                    <span className="font-medium truncate min-w-0">{other?.display_name}</span>
                    {isArchived ? (
                      <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground text-[10px] px-2 py-0.5"><Archive size={10} /> Ended</span>
                    ) : t.inbox_archived ? (
                      <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground text-[10px] px-2 py-0.5" data-testid="messages-thread-archived-badge"><Archive size={10} /> Archived</span>
                    ) : unreadCount > 0 && (
                      <span className="shrink-0 rounded-full bg-[hsl(var(--sb-clay))] text-white text-[10px] px-2 py-0.5">{unreadCount}</span>
                    )}
                  </div>
                  {t.job_id && (
                    <div
                      className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[hsl(var(--sb-gold-2))] truncate max-w-full"
                      title={t.job_title || "Untitled Project"}
                      data-testid="messages-thread-project-chip"
                    >
                      {t.job_title || "Untitled Project"}
                    </div>
                  )}
                  {t.origin === "community_update" && (
                    <div
                      className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[hsl(var(--sb-gold-2))] truncate max-w-full inline-flex items-center gap-1"
                      title={t.origin_title ? `Community Reply · ${t.origin_title}` : "Community Reply"}
                      data-testid="messages-thread-community-chip"
                    >
                      <Megaphone size={11} className="shrink-0" />
                      <span className="truncate">Community Reply{t.origin_title ? ` · ${t.origin_title}` : ""}</span>
                    </div>
                  )}
                  <div className="text-xs text-foreground/70 truncate max-w-full">{t.last_message || "New conversation"}</div>
                </div>
              </Link>
              {isCommunity && !isArchived && (
                <InboxArchiveButton thread={t} onDone={onUpdated} compact />
              )}
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

function ThreadView({ threadId, onUpdated }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [ending, setEnding] = useState(false);
  const lastSince = useRef(null);
  const scroller = useRef(null);
  // Sentinel element rendered as the *last* child of the messages list.
  // We scroll this into view on mount and after each new message. Using
  // scrollIntoView (rather than setting scrollTop on the container)
  // means the correct scroll parent wins whether that parent is the
  // messages container, the layout wrapper, or the browser window —
  // which is what actually happens on mobile where the layout height
  // cascade lets the window scroll instead of the internal panel.
  const endRef = useRef(null);
  // Tracks whether we've already snapped to the bottom for the current
  // thread. First jump is instant so a long conversation opens *at* the
  // latest message rather than smooth-scrolling from the top. Every
  // subsequent poll/send keeps the smooth-scroll behavior.
  const initialScrolled = useRef(false);

  const load = async (initial = false) => {
    try {
      const params = {};
      if (!initial && lastSince.current) params.since = lastSince.current;
      const { data } = await api.get(`/threads/${threadId}`, { params });
      setThread(data.thread);
      if (initial) setMessages(data.messages);
      else if (data.messages.length) setMessages((m) => [...m, ...data.messages]);
      if (data.messages.length) lastSince.current = data.messages[data.messages.length - 1].created_at;
      else if (initial) lastSince.current = new Date().toISOString();
      if (initial) onUpdated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    setThread(null); setMessages([]); lastSince.current = null;
    initialScrolled.current = false;
    load(true);
  }, [threadId]);

  useEffect(() => {
    const id = setInterval(() => load(false), 4000);
    return () => clearInterval(id);
  }, [threadId]);

  useEffect(() => {
    if (messages.length === 0) return;
    const isInitial = !initialScrolled.current;
    // requestAnimationFrame lets the freshly-mounted messages lay out
    // before we measure, otherwise very long histories land a few
    // hundred pixels short of the true bottom.
    requestAnimationFrame(() => {
      const el = endRef.current;
      if (!el) return;
      el.scrollIntoView({
        behavior: isInitial ? "auto" : "smooth",
        block: "end",
      });
      if (isInitial) initialScrolled.current = true;
    });
  }, [messages]);

  const composerRef = useRef(null);
  const send = async (e) => {
    e?.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    try {
      const { data } = await api.post(`/threads/${threadId}/messages`, { body });
      setMessages((m) => [...m, data]);
      setThread((t) => (t?.origin === "community_update" ? { ...t, inbox_archived: false } : t));
      lastSince.current = data.created_at;
      setDraft("");
      // Reset the auto-grown height so the composer collapses back to
      // its single-line default after sending a long paragraph.
      if (composerRef.current) composerRef.current.style.height = "auto";
      onUpdated?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const endChat = async () => {
    if (!window.confirm("End this coffee chat? The conversation will be archived (read-only). You can start a fresh chat for another project anytime.")) return;
    setEnding(true);
    try {
      const { data } = await api.post(`/threads/${threadId}/close`);
      setThread(data);
      toast.success("Chat ended — archived for reference.");
      onUpdated?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEnding(false); }
  };

  if (!thread) return <div className="p-6 text-sm text-muted-foreground">Loading conversation…</div>;

  const other = thread.participants.find((p) => p.id !== user?.id) || thread.participants[0];
  const isArchived = (thread.status || "active") === "archived";
  // Early Access read-only state — server tells us if the current viewer
  // or the counterparty has had EA revoked. Composer is hidden in either
  // case; existing history stays visible.
  const eaReadonly = thread.early_access_readonly || null;

  return (
    <div className="h-full flex flex-col min-w-0" data-testid={TID.messages.threadRoot}>
      <div className="p-4 border-b flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={() => navigate("/messages")}
          className="lg:hidden -ml-1 rounded-full p-1.5 text-muted-foreground hover:bg-muted transition-colors shrink-0"
          aria-label="Back to conversations"
          data-testid="messages-back-button"
        >
          <ArrowLeft size={18} />
        </button>
        <ParticipantAvatar participant={other} />
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{other?.display_name}</div>
          {thread.job_id && (
            <Link
              to={`/jobs/${thread.job_id}`}
              className="text-xs text-muted-foreground link-underline truncate block"
              title={thread.job_title || "Untitled Project"}
              data-testid="messages-thread-project-title"
            >
              Project: {thread.job_title || "Untitled Project"}
            </Link>
          )}
          {thread.origin === "community_update" && (
            <div
              className="text-xs text-muted-foreground truncate flex items-center gap-1"
              title={thread.origin_title ? `Community Reply · ${thread.origin_title}` : "Community Reply"}
              data-testid="messages-thread-community-title"
            >
              <Megaphone size={12} className="shrink-0" />
              <span className="truncate">Community Reply{thread.origin_title ? ` · ${thread.origin_title}` : ""}</span>
            </div>
          )}
        </div>
        {!isArchived && thread.origin !== "community_update" ? (
          <button
            onClick={endChat}
            disabled={ending}
            data-testid="messages-end-chat"
            className="btn-ghost border text-xs whitespace-nowrap disabled:opacity-60 shrink-0"
            title="End this coffee chat and archive the conversation"
            aria-label="End chat"
          >
            <PowerOff size={14} />
            <span className="hidden sm:inline">{ending ? "Ending…" : "End chat"}</span>
          </button>
        ) : isArchived ? (
          <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground text-xs px-3 py-1"><Archive size={12} /> <span className="hidden sm:inline">Ended</span></span>
        ) : (
          <InboxArchiveButton thread={thread} onDone={(t) => { setThread((cur) => ({ ...cur, inbox_archived: t.inbox_archived })); onUpdated?.(); }} />
        )}
        {other?.id && thread.origin !== "community_update" && (
          <div className="shrink-0">
            <ReportBlockMenu
              targetUserId={other.id}
              targetDisplayName={other.display_name}
              context={`thread:${threadId}`}
              size="sm"
            />
          </div>
        )}
      </div>
      {thread.origin === "community_update" && (
        <div
          className="px-4 py-3 border-b bg-[hsl(var(--sb-cream-2)/0.5)] border-[hsl(var(--sb-gold)/0.35)] flex items-start gap-3"
          data-testid="messages-community-update-banner"
        >
          <Megaphone size={16} className="mt-0.5 shrink-0 text-[hsl(var(--sb-gold-2))]" />
          <div className="text-xs md:text-sm">
            <div className="font-semibold text-foreground">
              Community Reply{thread.origin_title ? ` — ${thread.origin_title}` : ""}
            </div>
            <div className="text-muted-foreground">
              Private conversation about a Last Bookmark community update.
            </div>
          </div>
        </div>
      )}
      {!isArchived && thread.inbox_archived && (
        <div className="px-4 py-3 border-b bg-muted/40 text-xs text-muted-foreground flex items-start gap-2" data-testid="messages-inbox-archived-banner">
          <Archive size={14} className="mt-0.5 shrink-0" />
          <span>You archived this conversation, so it&apos;s hidden from your main inbox. A new reply will move it back automatically, or tap Restore.</span>
        </div>
      )}
      {isArchived && (
        <div className="px-4 py-3 border-b bg-muted/40 text-xs text-muted-foreground flex items-start gap-2" data-testid="messages-archived-banner">
          <Archive size={14} className="mt-0.5 shrink-0" />
          <span>
            This chat was ended{thread.archived_at ? ` on ${new Date(thread.archived_at).toLocaleDateString()}` : ""}. It&apos;s read-only now — but you can start a fresh coffee chat for another project anytime from a profile or project page.
          </span>
        </div>
      )}
      {!isArchived && eaReadonly && (
        <div
          className="px-4 py-3 border-b bg-[hsl(var(--sb-clay))]/10 border-[hsl(var(--sb-clay))]/30 text-xs md:text-sm text-foreground/85 flex items-start gap-2"
          data-testid="messages-ea-readonly-banner"
        >
          <ShieldOff size={14} className="mt-0.5 shrink-0 text-[hsl(var(--sb-clay))]" />
          <span>
            {eaReadonly.viewer_revoked
              ? "Your Early Access is no longer active. This conversation is now read-only."
              : `${eaReadonly.other_name || "This member"}'s Early Access is no longer active. You can't continue this conversation for now.`}
          </span>
        </div>
      )}
      <div ref={scroller} className="flex-1 overflow-y-auto p-4 space-y-2 min-h-[400px]">
        {messages.length === 0 && <div className="text-sm text-muted-foreground text-center py-16">Say hello to start the conversation.</div>}
        {messages.map((m) => {
          const isAdminReply = m.is_admin_reply;
          const mine = !isAdminReply && m.sender_id === user?.id;
          if (isAdminReply) {
            return (
              <div key={m.id} className="flex justify-center" data-testid={`message-admin-reply-${m.id}`}>
                <div className="max-w-[85%] px-4 py-3 rounded-2xl border-2 border-[hsl(var(--sb-gold))] bg-[hsl(var(--sb-gold))]/15 dark:bg-[hsl(var(--sb-gold))]/25 text-sm leading-relaxed whitespace-pre-wrap break-words shadow-sm">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-bold text-[hsl(var(--sb-ink))] dark:text-[hsl(40_60%_96%)]">
                    <ShieldCheck size={11} strokeWidth={2.5} /> {m.admin_display_name || "Last Bookmark Admin"}
                  </div>
                  <div className="mt-1.5 text-[hsl(var(--sb-ink))] dark:text-[hsl(40_60%_96%)]">
                    {m.body}
                  </div>
                  <div className="mt-1 text-[10px] text-[hsl(var(--sb-ink))]/70 dark:text-[hsl(40_60%_96%)]/70">
                    {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                mine ? "bg-[hsl(var(--sb-forest))] text-white rounded-br-sm"
                     : "bg-muted rounded-bl-sm"
              }`}>
                {m.body}
                <div className={`mt-1 text-[10px] opacity-70 ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                  {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
        {/* Sentinel: scrollIntoView(this) always lands the newest
            message at the bottom of the visible area regardless of
            which ancestor turns out to be the actual scroll parent. */}
        <div ref={endRef} aria-hidden="true" data-testid="messages-scroll-anchor" />
      </div>
      {!isArchived && !eaReadonly ? (
        <form onSubmit={send} className="p-3 border-t flex gap-2 items-end min-w-0">
          <textarea
            ref={composerRef}
            className="sb-input flex-1 min-w-0 resize-none py-2 leading-relaxed"
            placeholder="Write a message… (Enter for new line, Ctrl/⌘+Enter to send)"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              // Cmd/Ctrl + Enter sends. Plain Enter inserts a new line —
              // this prevents accidental sends when the user is composing
              // a paragraph, and matches how mobile keyboards naturally
              // treat the Return key.
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                send(e);
              }
            }}
            onInput={(e) => {
              // Auto-grow up to ~6 lines so the composer stays compact
              // but can expand for longer messages.
              const el = e.currentTarget;
              el.style.height = "auto";
              el.style.height = Math.min(el.scrollHeight, 168) + "px";
            }}
            rows={1}
            style={{ minHeight: 40, maxHeight: 168 }}
            data-testid={TID.messages.input}
          />
          <button type="submit" className="btn-primary shrink-0" disabled={sending} data-testid={TID.messages.send} aria-label="Send message">
            <Send size={16} />
            <span className="hidden sm:inline">{sending ? "…" : "Send"}</span>
          </button>
        </form>
      ) : eaReadonly ? (
        <div
          className="p-4 border-t text-center text-xs text-muted-foreground"
          data-testid="messages-ea-readonly-composer-note"
        >
          {eaReadonly.viewer_revoked
            ? "Your Early Access is no longer active. This conversation is now read-only."
            : `${eaReadonly.other_name || "This member"}'s Early Access is no longer active. This conversation is now read-only.`}
        </div>
      ) : (
        <div className="p-4 border-t text-center text-xs text-muted-foreground">
          This conversation is archived. Start a new chat from{" "}
          <Link to="/coffee-chats" className="link-underline">Coffee Chats</Link> or a project page.
        </div>
      )}
    </div>
  );
}

function InboxArchiveButton({ thread, onDone, compact = false }) {
  const [busy, setBusy] = useState(false);
  const archived = !!thread.inbox_archived;
  const label = archived ? "Restore" : "Archive";
  const toggle = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setBusy(true);
    try {
      const { data } = await api.post(`/threads/${thread.id}/${archived ? "inbox-restore" : "inbox-archive"}`);
      toast.success(archived ? "Moved back to your inbox" : "Conversation archived");
      onDone?.(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const Icon = archived ? ArchiveRestore : Archive;
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      aria-label={`${label} conversation`}
      title={archived ? "Restore to your inbox" : "Archive (only hides it from your inbox)"}
      data-testid={`${compact ? "messages-row" : "messages-thread"}-${archived ? "restore" : "archive"}-btn`}
      className={compact
        ? "shrink-0 mr-2 h-10 w-10 grid place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50"
        : "btn-ghost border text-xs whitespace-nowrap shrink-0 min-h-[40px] disabled:opacity-60"}
    >
      <Icon size={compact ? 16 : 14} />
      {!compact && <span className="hidden sm:inline">{busy ? "Saving…" : label}</span>}
    </button>
  );
}

function EmptyPane() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center p-10">
      <div className="mx-auto w-16 h-16 rounded-full bg-[hsl(var(--sb-moss))] grid place-items-center">
        <MessageCircle size={26} className="text-[hsl(var(--sb-forest-2))]" />
      </div>
      <h3 className="mt-5 font-serif text-2xl">Pick a conversation</h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-sm">
        Every project deserves a story of its own. Pick a thread from the left — or head to a project page and start a fresh one.
      </p>
    </div>
  );
}

function ParticipantAvatar({ participant }) {
  const url = participant?.avatar_url;
  const initial = (participant?.display_name || "?")[0]?.toUpperCase();
  if (url) return <img src={url} className="w-10 h-10 rounded-full object-cover" alt="" />;
  return (
    <div className="w-10 h-10 rounded-full bg-[hsl(var(--sb-moss))] grid place-items-center text-[hsl(var(--sb-forest-2))] font-serif font-semibold">
      {initial}
    </div>
  );
}
